import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from "@firebase/firestore";
import { dismissLoadingToast, showErrorToast, showLoadingToast, showSuccessToast, updateLoadingToast } from "app/components/ShowToast";
import axios from "axios";
import db from "services/firebase";

interface ConnectedApp {
  clientId: string;
  displayText?: string;
  kind?: string;
  nativeApp?: boolean;
  scopes?: string[];
  userKey?: string;
}

interface GoogleWorkspaceUser {
  id: string;
  primaryEmail: string;
  name: {
    givenName: string;
    familyName: string;
    fullName: string;
  };
  suspended: boolean;
  orgUnitPath: string;
  isAdmin: boolean;
  isDelegatedAdmin: boolean;
  isEnrolledIn2Sv: boolean;
  creationTime: string;
  lastLoginTime: string;
  connectedApps?: ConnectedApp[];
}

interface GoogleWorkspaceGroup {
  id: string;
  email: string;
  name: string;
  description: string;
  adminCreated: boolean;
  directMembersCount: number;
}

interface GoogleWorkspaceOrgUnit {
  orgUnitId: string;
  orgUnitPath: string;
  name: string;
  description: string;
  parentOrgUnitPath: string;
}

interface GoogleWorkspaceDomain {
  domainName: string;
  isPrimary: boolean;
  verified: boolean;
  creationTime: string;
}

interface GoogleWorkspaceDataResponse {
  success: boolean;
  users?: GoogleWorkspaceUser[];
  groups?: GoogleWorkspaceGroup[];
  orgUnits?: GoogleWorkspaceOrgUnit[];
  domains?: GoogleWorkspaceDomain[];
  error?: string;
}

interface GoogleWorkspaceError {
  response?: {
    status?: number;
    data?: {
      message?: string;
      error?: string;
    };
  };
  message?: string;
}

export const createGoogleWorkspaceIntegration = (accessToken: string) => {
  if (!accessToken || typeof accessToken !== "string" || accessToken.trim() === "") {
    throw new Error("A valid access token is required for Google Workspace integration");
  }

  const getUserFriendlyErrorMessage = (error: unknown): string => {
    const googleError = error as GoogleWorkspaceError;
    if (googleError.response?.status === 403) {
      return "Access forbidden. Please check your access token permissions and ensure domain-wide delegation is configured.";
    } else if (googleError.response?.status === 401) {
      return "Invalid access token. Please reconnect to Google Workspace.";
    } else if (googleError.response?.status === 404) {
      return "Resource not found. Please check the domain or resource ID.";
    } else if (googleError.response?.status === 429) {
      return "Google API rate limit exceeded. Please try again later.";
    } else if (googleError.message?.includes("ENOTFOUND") || googleError.message?.includes("Network Error")) {
      return "Unable to reach Google API. Please check your network connection and try again.";
    }
    return (
      googleError.response?.data?.message ||
      googleError.response?.data?.error ||
      googleError.message ||
      "An unexpected error occurred."
    );
  };

  const fetchWorkspaceData = async (): Promise<GoogleWorkspaceDataResponse> => {
    try {
      const response = await axios.post(
        `https://us-central1-slate-134a0.cloudfunctions.net/fetchGoogleWorkspaceData`,
        { accessToken },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      return response.data as GoogleWorkspaceDataResponse;
    } catch (error: any) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  return {
    fetchWorkspaceData,
  };
};

export const exchangeCodeForGoogleWorkspaceToken = async (
  code: string,
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
) => {
  let toastId;
  try {
    toastId = showLoadingToast({ message: "Authenticating with Google Workspace..." });
    const tokenResponse = await fetch(
      `https://us-central1-slate-134a0.cloudfunctions.net/googleWorkspaceOAuthCallback?code=${encodeURIComponent(code)}`,
      {
        method: "POST",
        mode: "cors",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const responseText = await tokenResponse.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      throw new Error("Invalid response format from server");
    }

    if (!data.access_token || typeof data.access_token !== "string" || data.access_token.trim() === "") {
      throw new Error("No valid access token received from server");
    }

    updateLoadingToast(toastId, { message: "Token received, setting up integration...", type: "info", isLoading: true });
    localStorage.setItem("google_workspace_access_token", data.access_token);
    if (data.refresh_token) {
      localStorage.setItem("google_workspace_refresh_token", data.refresh_token);
    }

    const googleWorkspaceIntegration = createGoogleWorkspaceIntegration(data.access_token);
    updateLoadingToast(toastId, { message: "Fetching workspace data...", type: "info", isLoading: true });
    const syncResult = await googleWorkspaceIntegration.fetchWorkspaceData();

    if (!syncResult.success) {
      throw new Error(syncResult.error || "Failed to sync workspace data");
    }

    updateLoadingToast(toastId, { message: "Saving workspace data...", type: "info", isLoading: true });
    const integrationId = await fetchAndSaveGoogleWorkspaceData(
      data.access_token,
      syncResult.users || [],
      syncResult.groups || [],
      syncResult.orgUnits || [],
      syncResult.domains || [],
      currentUser,
      navigate
    );
    if (!integrationId) {
      throw new Error("Failed to create integration");
    }

    dismissLoadingToast(toastId);
    showSuccessToast({ message: "Successfully authenticated with Google Workspace and retrieved workspace data" });
    navigate(`/integrations?id=${integrationId}&service=Google Workspace`, { replace: true });
  } catch (error: any) {
    showErrorToast({ message: "Google Workspace authentication or sync failed. Check console for details." });
    dismissLoadingToast(toastId);
    localStorage.removeItem("google_workspace_access_token");
    localStorage.removeItem("google_workspace_refresh_token");
    navigate("/integrations", { replace: true });
    throw error;
  }
};

export const fetchAndSaveGoogleWorkspaceData = async (
  accessToken: string,
  users: GoogleWorkspaceUser[],
  groups: GoogleWorkspaceGroup[],
  orgUnits: GoogleWorkspaceOrgUnit[],
  domains: GoogleWorkspaceDomain[],
  currentUser: any,
  navigate: (path: string, options?: { replace: boolean }) => void
): Promise<string | undefined> => {
  try {
    const currentDate = new Date();
    const formattedDate = `${currentDate.getFullYear()}-${String(
      currentDate.getMonth() + 1
    ).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    let integrationId: string | undefined;

    const tenantRef = doc(db, "tenants", currentUser?.tenantId);
    const integrationsRef = collection(tenantRef, "integrations");
    const vendorsRef = collection(tenantRef, "vendors");
    const accessReviewRef = doc(tenantRef, "access_review", "google_workspace");
    const integrationSnapshot = await getDocs(
      query(integrationsRef, where("name", "==", "Google Workspace"))
    );

    const oauthClientId = process.env.REACT_APP_GOOGLE_WORKSPACE_CLIENT_ID || '';
    
    if (integrationSnapshot.empty) {
      const newData = {
        accessToken: accessToken,
        name: "Google Workspace",
        group: "Business Suite",
        img_path: "/integrations/google GSuite.svg",
        status: "Connected",
        connectedAt: formattedDate,
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
        userCount: users.length,
        groupCount: groups.length,
        orgUnitCount: orgUnits.length,
        domainCount: domains.length,
        oauthClientId: oauthClientId, 
      };

      const docRef = await addDoc(integrationsRef, newData);
      integrationId = docRef.id;

      const vendorData = {
        authentication_type: "OAuth",
        category: "Business Suite",
        created_at: serverTimestamp(),
        due_diligence_status: "Incomplete",
        impact_assessment: [],
        logo: [
          {
            name: "google_workspace_logo.png",
            size: 5000, 
            url: "/integrations/google GSuite.svg",
          },
        ],
        owner: currentUser?.userName || "",
        policy_url: "https://workspace.google.com/terms/",
        reliance: "",
        risk_level: "",
        tenantId: currentUser?.tenantId,
        terms_url: "https://workspace.google.com/terms/",
        two_factor: true,
        uid: currentUser?.uid,
        updated_at: serverTimestamp(),
        vendor_name: "Google Workspace",
        website_url: "https://workspace.google.com",
      };

      await addDoc(vendorsRef, vendorData);
    } else {
      integrationId = integrationSnapshot.docs[0].id;
    }

    const allConnectedApps: { [clientId: string]: ConnectedApp } = {};
    
    // Save users to access_review collection
    const membersMap: { [key: string]: any } = {};
    for (const user of users || []) {
      if (user.id && user.primaryEmail) {
        const accountRole = user.isAdmin 
          ? "Admin" 
          : user.isDelegatedAdmin 
          ? "Delegated Admin" 
          : "User";
        
        if (user.connectedApps && user.connectedApps.length > 0) {
          user.connectedApps.forEach((app: ConnectedApp) => {
            if (app.clientId && !allConnectedApps[app.clientId]) {
              allConnectedApps[app.clientId] = app;
            }
          });
        }
        
        membersMap[user.id] = {
          id: user.id,
          email: user.primaryEmail,
          name: user.name?.fullName || `${user.name?.givenName || ''} ${user.name?.familyName || ''}`.trim() || user.primaryEmail,
          givenName: user.name?.givenName || '',
          familyName: user.name?.familyName || '',
          accountRole: accountRole,
          isAdmin: user.isAdmin,
          isDelegatedAdmin: user.isDelegatedAdmin,
          isEnrolledIn2Sv: user.isEnrolledIn2Sv,
          suspended: user.suspended,
          orgUnitPath: user.orgUnitPath || '/',
          creationTime: user.creationTime,
          lastLoginTime: user.lastLoginTime,
          connectedApps: user.connectedApps || [],
          platform: "Google Workspace",
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: user.suspended ? 'suspended' : 'active',
        };
      }
    }

    // Save or update access review document
    const googleWorkspaceDoc = await getDoc(accessReviewRef);
    if (googleWorkspaceDoc.exists()) {
      await updateDoc(accessReviewRef, {
        members: {
          ...googleWorkspaceDoc.data().members,
          ...membersMap,
        },
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(accessReviewRef, {
        platform: "Google Workspace",
        members: membersMap,
        groups: groups.map(group => ({
          id: group.id,
          email: group.email,
          name: group.name,
          description: group.description,
          memberCount: group.directMembersCount,
        })),
        orgUnits: orgUnits.map(ou => ({
          orgUnitId: ou.orgUnitId,
          orgUnitPath: ou.orgUnitPath,
          name: ou.name,
          description: ou.description,
          parentOrgUnitPath: ou.parentOrgUnitPath,
        })),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    // Save connected apps as vendors
    if (Object.keys(allConnectedApps).length > 0) {
      const integrationDoc = integrationSnapshot.empty ? null : integrationSnapshot.docs[0];
      const integrationClientId = integrationDoc?.data()?.oauthClientId || 
                                   process.env.REACT_APP_GOOGLE_WORKSPACE_CLIENT_ID || 
                                   '';
      
      // System/browser OAuth scopes that indicate internal Google apps (not third-party vendors)
      const systemScopes = [
        'https://www.google.com/accounts/OAuthLogin', // Google Chrome and system apps
      ];
      
      for (const [clientId, app] of Object.entries(allConnectedApps)) {
        if (!clientId) {
          continue;
        }
        
        const vendorName = app.displayText || '';
        
        // Skip if no name
        if (!vendorName || vendorName.trim() === '') {
          continue;
        }
        
        // Exclude the integration's own OAuth app 
        if (integrationClientId && clientId === integrationClientId) {
          continue;
        }
        
        if (integrationClientId) {
          const integrationProjectNumber = integrationClientId.split('-')[0];
          const appProjectNumber = clientId.split('-')[0];
          if (integrationProjectNumber && appProjectNumber === integrationProjectNumber) {
            // Check if it's likely the same app (same project number and similar structure)
            continue;
          }
        }
        
        const appScopes = app.scopes || [];
        
        // Exclude apps with only system scopes (browsers, internal Google apps)
        const hasOnlySystemScopes = appScopes.length > 0 && 
          appScopes.every(scope => systemScopes.includes(scope));
        if (hasOnlySystemScopes) {
          continue;
        }
        
        // Exclude test apps: only spreadsheets + mail.google.com with no other meaningful scopes
        // This is a common pattern for test/development apps
        const hasSpreadsheets = appScopes.includes('https://www.googleapis.com/auth/spreadsheets');
        const hasMailGoogle = appScopes.includes('https://mail.google.com/');
        const hasOnlySpreadsheetsAndMail = hasSpreadsheets && hasMailGoogle && appScopes.length === 2;
        if (hasOnlySpreadsheetsAndMail) {
          continue;
        }
        
        // Check if vendor already exists for this client ID
        const existingVendorQuery = query(
          vendorsRef,
          where("clientId", "==", clientId),
          where("tenantId", "==", currentUser?.tenantId)
        );
        const existingVendorSnapshot = await getDocs(existingVendorQuery);
        
        const connectedAppVendorData = {
          clientId: clientId,
          vendor_name: vendorName,
          authentication_type: "OAuth",
          category: "",
          created_at: serverTimestamp(),
          due_diligence_status: "Incomplete",
          impact_assessment: [],
          logo: [],
          owner: currentUser?.userName || "",
          policy_url: "",
          reliance: "",
          risk_level: "",
          tenantId: currentUser?.tenantId,
          terms_url: "",
          two_factor: false,
          uid: currentUser?.uid,
          updated_at: serverTimestamp(),
          website_url: "",
          scopes: appScopes,
          nativeApp: app.nativeApp || false,
          kind: app.kind || '',
          source_platform: "Google Workspace",
          integration_id: integrationId,
        };

        if (existingVendorSnapshot.empty) {
          // Create new vendor document for connected app
          await addDoc(vendorsRef, connectedAppVendorData);
        } else {
          // Update existing vendor document
          const existingDoc = existingVendorSnapshot.docs[0];
          await updateDoc(doc(vendorsRef, existingDoc.id), {
            ...connectedAppVendorData,
            created_at: existingDoc.data().created_at, 
          });
        }
      }
    }

    // Save domains to domains collection
    if (domains && domains.length > 0 && integrationId) {
      const domainsRef = collection(tenantRef, "domain");
      
      for (const domain of domains) {
        if (domain.domainName) {
          // Check if domain already exists
          const existingDomainQuery = query(
            domainsRef,
            where("domainName", "==", domain.domainName),
            where("tenantId", "==", currentUser?.tenantId)
          );
          const existingDomainSnapshot = await getDocs(existingDomainQuery);

          const domainData = {
            name: domain.domainName,
            platform: "Google Workspace",
            type: "domain",
            domainName: domain.domainName,
            isPrimary: domain.isPrimary || false,
            verified: domain.verified || false,
            creationTime: domain.creationTime || '',
            integration_id: integrationId,
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          if (existingDomainSnapshot.empty) {
            // Create new domain document
            await addDoc(domainsRef, domainData);
          } else {
            // Update existing domain document
            const existingDoc = existingDomainSnapshot.docs[0];
            await updateDoc(doc(domainsRef, existingDoc.id), {
              ...domainData,
              createdAt: existingDoc.data().createdAt, // Preserve original creation time
            });
          }
        }
      }
    }

    return integrationId;
  } catch (error) {
    showErrorToast({ message: "Error setting up Google Workspace integration" });
    navigate("/integrations", { replace: true });
    throw error;
  }
};

